import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  LineChart, Line, CartesianGrid, PieChart, Pie, Cell,
} from 'recharts';
import { TrendingUp, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { getShareOfShelf, getChains } from '../lib/api';

const COLORS_OWN = '#059669';   // emerald
const COLORS_COMP = '#dc2626';  // red

export default function ShareOfShelfPage() {
  const [data, setData] = useState(null);
  const [chains, setChains] = useState([]);
  const [chainId, setChainId] = useState('all');
  const [days, setDays] = useState('30');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days: Number(days) };
      if (chainId && chainId !== 'all') params.chain_id = chainId;
      const [res, chainsRes] = await Promise.all([
        getShareOfShelf(params),
        getChains(),
      ]);
      setData(res.data);
      if (chainsRes.data) setChains(chainsRes.data);
    } catch (e) {
      toast.error('Error cargando Share-of-Shelf');
    } finally {
      setLoading(false);
    }
  }, [chainId, days]);

  useEffect(() => { load(); }, [load]);

  const global = data?.global || { own_facing: 0, comp_facing: 0, share_pct: 0, total_analyses: 0 };
  const pieData = [
    { name: 'Nuestros', value: global.own_facing, fill: COLORS_OWN },
    { name: 'Competencia', value: global.comp_facing, fill: COLORS_COMP },
  ];
  const byChain = data?.by_chain || [];
  const byStore = (data?.by_store || []).slice(0, 15);
  const daily = data?.daily || [];

  return (
    <div className="space-y-6" data-testid="sos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Share-of-Shelf</h1>
          <p className="text-slate-500">% de caras nuestras vs competencia · detectado por IA</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={chainId} onValueChange={setChainId}>
            <SelectTrigger className="w-[180px]" data-testid="sos-chain-filter">
              <SelectValue placeholder="Cadena" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las cadenas</SelectItem>
              {chains.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[140px]" data-testid="sos-days-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 días</SelectItem>
              <SelectItem value="14">Últimos 14 días</SelectItem>
              <SelectItem value="30">Últimos 30 días</SelectItem>
              <SelectItem value="60">Últimos 60 días</SelectItem>
              <SelectItem value="90">Últimos 90 días</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="sos-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refrescar
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <p className="text-xs text-emerald-700">Share global</p>
            <p className="text-3xl font-bold text-emerald-900" data-testid="sos-global">
              {global.share_pct}%
            </p>
            <p className="text-xs text-emerald-700 mt-1">
              {global.own_facing} caras nuestras
            </p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-xs text-red-700">Competencia</p>
            <p className="text-3xl font-bold text-red-900">
              {(100 - global.share_pct).toFixed(1)}%
            </p>
            <p className="text-xs text-red-700 mt-1">
              {global.comp_facing} caras competencia
            </p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Fotos analizadas</p>
            <p className="text-3xl font-bold text-slate-900">{global.total_analyses}</p>
            <p className="text-xs text-slate-500 mt-1">en {data?.period_days || 30} días</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Cadenas con presencia</p>
            <p className="text-3xl font-bold text-slate-900">{byChain.length}</p>
            <p className="text-xs text-slate-500 mt-1">{byStore.length} tiendas</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie chart global */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="w-4 h-4 text-emerald-600" /> Distribución global
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-64" /> : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={90} label>
                    {pieData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Daily line chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Evolución diaria del Share %</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-64" /> : daily.length === 0 ? (
              <p className="text-center text-slate-500 py-20 text-sm">
                Sin datos en este período. Analiza fotos desde Vigilancia IA → Panel IA.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={daily}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="share_pct" stroke={COLORS_OWN} strokeWidth={2} name="Share %" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* By chain bar chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Share % por Cadena</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <Skeleton className="h-64" /> : byChain.length === 0 ? (
            <p className="text-center text-slate-500 py-12 text-sm">Sin datos por cadena.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(260, byChain.length * 40)}>
              <BarChart data={byChain} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="chain_name" tick={{ fontSize: 11 }} width={120} />
                <Tooltip />
                <Bar dataKey="share_pct" fill={COLORS_OWN} name="Share %" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Worst stores */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            Tiendas con menor share (top {byStore.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <Skeleton className="h-40" /> : byStore.length === 0 ? (
            <p className="text-center text-slate-500 py-8 text-sm">Sin datos.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tienda</TableHead>
                  <TableHead>Cadena</TableHead>
                  <TableHead className="text-right">Nuestras</TableHead>
                  <TableHead className="text-right">Competencia</TableHead>
                  <TableHead className="text-right">Share %</TableHead>
                  <TableHead className="text-right">Análisis</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byStore.map((s) => (
                  <TableRow key={s.store_id} data-testid={`sos-store-row-${s.store_id}`}>
                    <TableCell className="font-medium">{s.store_name}</TableCell>
                    <TableCell className="text-slate-500">{s.chain_name || '-'}</TableCell>
                    <TableCell className="text-right text-emerald-700">{s.own_facing}</TableCell>
                    <TableCell className="text-right text-red-700">{s.comp_facing}</TableCell>
                    <TableCell className="text-right">
                      <Badge className={
                        s.share_pct >= 50 ? 'bg-emerald-600'
                          : s.share_pct >= 30 ? 'bg-amber-600' : 'bg-red-600'
                      }>
                        {s.share_pct}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-slate-500">{s.analyses}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
